import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContextProvider } from './contexts/AuthContext';
import { PartyContextProvider } from './contexts/PartyContext';
import { FriendsContextProvider } from './contexts/FriendsContext';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { LanguageContextProvider } from './contexts/LanguageContext';
import { useAuth } from './hooks/useAuth';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, CircularProgress } from '@mui/material';

// Theme
import lightTheme from './theme';
import { useTheme } from './hooks/useTheme';

// Components
import Navbar from './components/Navbar';
import JoinPartyByCode from './components/JoinPartyByCode';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import CreateParty from './pages/CreateParty';
import PartyDetail from './pages/PartyDetail';
import Friends from './pages/Friends';
import Profile from './pages/Profile';
import ResetPassword from './pages/ResetPassword';

// Protected route component
const RequireAuth = ({ children }) => {
  const { user, authIsReady } = useAuth();
  
  if (!authIsReady) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Theme selector component
const ThemedApp = () => {
  const { darkMode } = useTheme();
  
  // Create dark theme based on light theme
  const darkTheme = createTheme({
    ...lightTheme,
    palette: {
      ...lightTheme.palette,
      mode: 'dark',
      background: {
        default: '#121212',
        paper: '#1E1E1E',
        dark: '#121212',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#B0B0B0',
        disabled: '#6B6B6B',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
    },
  });
  
  // Select theme based on darkMode state
  const theme = darkMode ? darkTheme : lightTheme;
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContextProvider>
        <PartyContextProvider>
          <FriendsContextProvider>
            <BrowserRouter>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  minHeight: '100vh',
                  bgcolor: 'background.default'
                }}
              >
                <Navbar />
                <Container 
                  maxWidth="lg" 
                  sx={{ 
                    flexGrow: 1, 
                    py: 4,
                    px: { xs: 2, sm: 3 }
                  }}
                >
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route 
                      path="/create-party" 
                      element={
                        <RequireAuth>
                          <CreateParty />
                        </RequireAuth>
                      } 
                    />
                    <Route 
                      path="/edit-party/:id" 
                      element={
                        <RequireAuth>
                          <CreateParty isEditMode={true} />
                        </RequireAuth>
                      } 
                    />
                    <Route 
                      path="/party/:id" 
                      element={
                        <RequireAuth>
                          <PartyDetail />
                        </RequireAuth>
                      } 
                    />
                    <Route 
                      path="/join" 
                      element={
                        <RequireAuth>
                          <JoinPartyByCode />
                        </RequireAuth>
                      } 
                    />
                    <Route 
                      path="/friends" 
                      element={
                        <RequireAuth>
                          <Friends />
                        </RequireAuth>
                      } 
                    />
                    <Route 
                      path="/profile" 
                      element={
                        <RequireAuth>
                          <Profile />
                        </RequireAuth>
                      } 
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </Container>
              </Box>
            </BrowserRouter>
          </FriendsContextProvider>
        </PartyContextProvider>
      </AuthContextProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <LanguageContextProvider>
      <ThemeContextProvider>
        <ThemedApp />
      </ThemeContextProvider>
    </LanguageContextProvider>
  );
}

export default App;
